'use client'

// Drone camera — free-fly mode entered by pressing F (fly).
//
// Two pieces wired together via a module-local `droneInput` ref:
//   • DroneOverlay (DOM-side, mount in app/page.jsx) — owns the global
//     keydown listener for entry/exit + WASD/arrows/Space/Shift, and
//     renders the on-screen controls hint.
//   • DroneCameraDriver (Canvas-side, mount inside <Canvas> alongside
//     CameraController) — useFrame loop that reads droneInput each
//     frame and writes camera position + rotation, sliding against the
//     world's obstacle boxes so you can't fly through walls.
//
// Camera ownership while droneActive:
//   - FirstPersonController bails (MainScene stops passing enabled)
//   - DroneCameraDriver writes pos + quat directly
// On exit: the walker takes the camera back at the position and heading it
// held before you took off, since its walk state persists across the flight.
//
// Coordinate scheme (matches the rest of the project):
//   - Y is up, world-relative
//   - W/S translate along camera's full forward (pitch affects movement
//     direction — look down + W flies you toward ground)
//   - A/D rotate yaw (turn)
//   - ↑/↓ arrows pitch the view up/down (desktop look — no mouse)
//   - Space goes world-up, Ctrl/C goes world-down (gimbal-stabilised
//     vertical, like a quadcopter)
//   - Shift = sprint multiplier
//   The whole rig is keyboard-driven; the mouse is not used for looking.
//
// Wall collision: the drone slides against the same obstacle AABBs the
// bird uses (obstacles.glb, world-space boxes from ObstacleProvider).
// Checked in full 3D so you can still fly OVER rooftops — a box only
// blocks when the drone is within its vertical span.
//
// Plane feel (visual only — these don't affect movement direction):
//   - Roll banks toward the turn (lerp in, lerp back to level)
//   - Pitch tilts nose-up/-down with Space/Ctrl
//   - Light turbulence: gentle multi-frequency sine wobble on rotation
//     and position so it feels like wind, not a perfect rail

import { useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import useGameStore from '../../stores/gameStore'
import { useObstacles } from './obstacles/ObstacleProvider'
import { ui, kawaii } from '../../lib/ui/theme'

// Module-level shared state — both Overlay (writer) and Driver (reader)
// import this. Cheaper than putting high-frequency input in gameStore
// (which would re-render every frame).
const droneInput = {
  forward: false,
  back: false,
  left: false,
  right: false,
  up: false,
  down: false,
  sprint: false,
  // Look — driven by the ↑/↓ arrow keys (no mouse).
  pitchUp: false,
  pitchDown: false,
  // Yaw + pitch (radians). Integrated by Driver from key holds; read
  // by Driver to compose the camera quaternion.
  yaw: 0,
  pitch: 0,
}

// Tunables
const BASE_SPEED = 12          // metres / sec
const SPRINT_MULT = 3          // shift hold
const YAW_SPEED = 1.7          // rad / sec when A/D held (full 360° in ~3.7s)
const PITCH_SPEED = 1.4        // rad / sec when ↑/↓ held
const PITCH_LIMIT = (85 * Math.PI) / 180  // ±85° to avoid flipping
const TAU = Math.PI * 2

// Collision — margin around each obstacle box. The camera is a point but
// has a near clip plane; keep some daylight so walls don't clip into view.
const DRONE_COLLIDER_MARGIN = 0.6

// Plane-feel — visual only; doesn't affect movement direction.
const BANK_ANGLE = 0.42        // ~24° max bank when holding A/D
const BANK_LERP_RATE = 3.4     // higher = snappier bank in/out
const VERTICAL_TILT_ANGLE = 0.18 // ~10° nose-up/-down with Space/Ctrl
const VERTICAL_TILT_LERP = 4.0
// Light-turbulence — gentle multi-frequency wobble, easy to dial down.
const TURB_POS_AMP = 0.07      // metres — peak position drift around path
const TURB_ROT_AMP = 0.011     // radians — peak rotational shake (~0.6°)
const TURB_ROLL_BOOST = 1.5    // roll wobbles a touch more than pitch/yaw

// Reusable temporaries (no per-frame allocations)
const _forward = new THREE.Vector3()
const _move = new THREE.Vector3()
const _euler = new THREE.Euler(0, 0, 0, 'YXZ')  // yaw before pitch

// Plane-feel state. Module-level so re-mounts don't re-allocate.
// Reset by the entry useEffect each time droneActive flips on.
const droneState = {
  roll: 0,
  tilt: 0,
  turbT: 0,
  // Last-frame turbulence offsets so we apply them as deltas
  // (camera oscillates around its intended position instead of drifting).
  lastTurbX: 0,
  lastTurbY: 0,
  lastTurbZ: 0,
}

// Is (x,y,z) inside any obstacle box (with margin)? Full 3D so the drone
// can fly over rooftops — a box only counts when we're within its Y span.
function insideAnyObstacle(x, y, z, obstacles, M = DRONE_COLLIDER_MARGIN) {
  for (let i = 0; i < obstacles.length; i++) {
    const b = obstacles[i]?.box
    if (!b) continue
    if (
      x > b.min.x - M && x < b.max.x + M &&
      z > b.min.z - M && z < b.max.z + M &&
      y > b.min.y - M && y < b.max.y + M
    ) {
      return true
    }
  }
  return false
}

// ─────────────────────────────────────────────────────────────────────
// CANVAS-SIDE: drives the camera each frame from droneInput.
// Mount inside <Canvas> alongside CameraController.
// ─────────────────────────────────────────────────────────────────────

// Where the drone was parked on last exit — re-entering resumes the flight
// instead of taking over from wherever the current camera happens to be.
const droneResume = { has: false, x: 0, y: 0, z: 0, yaw: 0, pitch: 0 }

export function DroneCameraDriver() {
  const { camera } = useThree()
  const droneActive = useGameStore((s) => s.droneActive)
  const { obstacles } = useObstacles()

  // On entering drone mode: resume from where the drone was last parked
  // (position + yaw/pitch survive exits for the session); on a first
  // flight, capture the current camera state instead so the takeover
  // doesn't snap to (0,0).
  useEffect(() => {
    if (!droneActive) return
    if (droneResume.has) {
      camera.position.set(droneResume.x, droneResume.y, droneResume.z)
      droneInput.yaw = droneResume.yaw
      droneInput.pitch = droneResume.pitch
    } else {
      // Decompose current camera quaternion into yaw + pitch (Y-up, YXZ).
      _euler.setFromQuaternion(camera.quaternion, 'YXZ')
      droneInput.yaw = _euler.y
      droneInput.pitch = THREE.MathUtils.clamp(_euler.x, -PITCH_LIMIT, PITCH_LIMIT)
    }
    // Reset transient input so old key holds don't carry through.
    droneInput.forward = droneInput.back = false
    droneInput.left = droneInput.right = false
    droneInput.up = droneInput.down = droneInput.sprint = false
    droneInput.pitchUp = droneInput.pitchDown = false
    camera.up.set(0, 1, 0)
    // Reset plane-feel state — fresh bank, no carry-over wobble.
    droneState.roll = 0
    droneState.tilt = 0
    droneState.turbT = 0
    droneState.lastTurbX = droneState.lastTurbY = droneState.lastTurbZ = 0
    // On exit, park the drone here so re-entering resumes the flight.
    return () => {
      droneResume.has = true
      droneResume.x = camera.position.x
      droneResume.y = camera.position.y
      droneResume.z = camera.position.z
      droneResume.yaw = droneInput.yaw
      droneResume.pitch = droneInput.pitch
    }
  }, [droneActive, camera])

  useFrame((_, dt) => {
    if (!droneActive) return
    const safeDt = Math.min(dt, 0.05)

    // A/D rotate (keyboard yaw). Track signed turn input for banking:
    // A=+1, D=-1, both/neither=0.
    let turnInput = 0
    if (droneInput.left)  { droneInput.yaw += YAW_SPEED * safeDt; turnInput += 1 }
    if (droneInput.right) { droneInput.yaw -= YAW_SPEED * safeDt; turnInput -= 1 }

    // Vertical tilt input — Space/Ctrl tip nose up/down for plane feel.
    let vertInput = 0
    if (droneInput.up)   vertInput += 1
    if (droneInput.down) vertInput -= 1

    // ↑/↓ arrows → pitch (look up/down). Keyboard-only; no mouse look.
    if (droneInput.pitchUp)   droneInput.pitch += PITCH_SPEED * safeDt
    if (droneInput.pitchDown) droneInput.pitch -= PITCH_SPEED * safeDt
    if (droneInput.pitch > PITCH_LIMIT) droneInput.pitch = PITCH_LIMIT
    if (droneInput.pitch < -PITCH_LIMIT) droneInput.pitch = -PITCH_LIMIT

    // Wrap yaw — keeps it bounded (prevents float drift over hours).
    while (droneInput.yaw > Math.PI) droneInput.yaw -= TAU
    while (droneInput.yaw < -Math.PI) droneInput.yaw += TAU

    // Lerp roll toward target bank, tilt toward vertical-input target.
    // Frame-rate-independent exp smoothing (alpha = 1 - e^(-rate·dt)).
    const targetRoll = turnInput * BANK_ANGLE
    const targetTilt = vertInput * VERTICAL_TILT_ANGLE
    const bankAlpha = 1 - Math.exp(-BANK_LERP_RATE * safeDt)
    const tiltAlpha = 1 - Math.exp(-VERTICAL_TILT_LERP * safeDt)
    droneState.roll += (targetRoll - droneState.roll) * bankAlpha
    droneState.tilt += (targetTilt - droneState.tilt) * tiltAlpha

    // Light turbulence — multi-frequency sine wobble. Two octaves blended
    // gives a less-periodic, more wind-buffeted feel than a single sine.
    droneState.turbT += safeDt
    const t = droneState.turbT
    const turbPitch = (Math.sin(t * 1.20 + 0.30) * 0.6 + Math.sin(t * 2.70 + 1.70) * 0.3) * TURB_ROT_AMP
    const turbYaw   = (Math.sin(t * 0.90 + 1.10) * 0.5 + Math.sin(t * 2.10 + 3.40) * 0.3) * TURB_ROT_AMP
    const turbRoll  = (Math.sin(t * 1.40 + 2.20) * 0.7 + Math.sin(t * 3.10 + 0.60) * 0.3) * TURB_ROT_AMP * TURB_ROLL_BOOST

    // Compose camera rotation: pitch = arrows + vertical tilt + turbulence;
    // yaw = keyboard + turbulence; roll = bank + turbulence (YXZ order).
    const finalPitch = THREE.MathUtils.clamp(
      droneInput.pitch + droneState.tilt + turbPitch,
      -PITCH_LIMIT, PITCH_LIMIT,
    )
    _euler.set(finalPitch, droneInput.yaw + turbYaw, droneState.roll + turbRoll, 'YXZ')
    camera.quaternion.setFromEuler(_euler)

    // Build movement vector. Forward follows the camera's looking
    // direction (pitch + yaw); roll/tilt/turbulence are visual only and
    // don't affect where W flies you. Up/down stays world-Y so banking
    // doesn't bleed altitude.
    camera.getWorldDirection(_forward)

    _move.set(0, 0, 0)
    if (droneInput.forward) _move.add(_forward)
    if (droneInput.back)    _move.sub(_forward)
    if (droneInput.up)      _move.y += 1
    if (droneInput.down)    _move.y -= 1

    if (_move.lengthSq() > 0) {
      _move.normalize()
      const speed = BASE_SPEED * (droneInput.sprint ? SPRINT_MULT : 1)
      _move.multiplyScalar(speed * safeDt)

      // Wall collision — slide axis-by-axis against the obstacle boxes.
      // Each axis only commits if it doesn't put the camera inside a box,
      // so hitting a wall head-on slides you along it instead of stopping
      // dead. Vertical is checked too (can't sink through a rooftop), but
      // above the roof the 3D test passes and you fly free.
      const pos = camera.position
      if (obstacles && obstacles.length > 0) {
        let nx = pos.x + _move.x
        if (insideAnyObstacle(nx, pos.y, pos.z, obstacles)) nx = pos.x
        let nz = pos.z + _move.z
        if (insideAnyObstacle(nx, pos.y, nz, obstacles)) nz = pos.z
        let ny = pos.y + _move.y
        if (insideAnyObstacle(nx, ny, nz, obstacles)) ny = pos.y
        pos.set(nx, ny, nz)
      } else {
        pos.add(_move)
      }
    }

    // Position turbulence — applied as a delta so it oscillates the
    // camera around its intended path instead of drifting it. Vertical
    // bobble is dampened so we don't feel like we're losing height.
    const newTurbX = (Math.sin(t * 0.70 + 0.50) * 0.6 + Math.sin(t * 1.90 + 2.30) * 0.3) * TURB_POS_AMP
    const newTurbY = (Math.sin(t * 0.60 + 1.40) * 0.6 + Math.sin(t * 2.30 + 4.10) * 0.3) * TURB_POS_AMP * 0.55
    const newTurbZ = (Math.sin(t * 0.85 + 3.20) * 0.6 + Math.sin(t * 1.70 + 0.80) * 0.3) * TURB_POS_AMP
    camera.position.x += newTurbX - droneState.lastTurbX
    camera.position.y += newTurbY - droneState.lastTurbY
    camera.position.z += newTurbZ - droneState.lastTurbZ
    droneState.lastTurbX = newTurbX
    droneState.lastTurbY = newTurbY
    droneState.lastTurbZ = newTurbZ
  })

  return null
}

// ─────────────────────────────────────────────────────────────────────
// DOM-SIDE: keydown listener + on-screen overlay.
// Mount in app/page.jsx (outside the <Canvas>).
// ─────────────────────────────────────────────────────────────────────

export function DroneOverlay() {
  const droneActive = useGameStore((s) => s.droneActive)
  const setDroneActive = useGameStore((s) => s.setDroneActive)

  // Global keydown — owns 'f' (enter/fly), 'Escape' (exit), and movement +
  // look keys while active. (Entry moved off 'd' so first-person can use A/D
  // to turn.)
  useEffect(() => {
    const onDown = (e) => {
      // Skip when typing in inputs / textareas / contenteditable.
      const t = e.target
      const tag = t?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return

      // Enter drone mode (only when not already in it).
      if (!droneActive) {
        if (e.key === 'f' || e.key === 'F') {
          e.preventDefault()
          setDroneActive(true)
        }
        return
      }

      // Inside drone mode — capture movement + look keys.
      switch (e.code) {
        case 'KeyW': droneInput.forward = true; e.preventDefault(); break
        case 'KeyS': droneInput.back    = true; e.preventDefault(); break
        case 'KeyA': droneInput.left    = true; e.preventDefault(); break
        case 'KeyD': droneInput.right   = true; e.preventDefault(); break
        case 'ArrowUp':   droneInput.pitchUp   = true; e.preventDefault(); break
        case 'ArrowDown': droneInput.pitchDown = true; e.preventDefault(); break
        case 'Space':       droneInput.up = true; e.preventDefault(); break
        case 'ControlLeft':
        case 'ControlRight':
        case 'KeyC':        droneInput.down = true; e.preventDefault(); break
        case 'ShiftLeft':
        case 'ShiftRight':  droneInput.sprint = true; break
        case 'Escape':
          setDroneActive(false)
          break
      }
    }
    const onUp = (e) => {
      if (!droneActive) return
      switch (e.code) {
        case 'KeyW': droneInput.forward = false; break
        case 'KeyS': droneInput.back    = false; break
        case 'KeyA': droneInput.left    = false; break
        case 'KeyD': droneInput.right   = false; break
        case 'ArrowUp':   droneInput.pitchUp   = false; break
        case 'ArrowDown': droneInput.pitchDown = false; break
        case 'Space':       droneInput.up = false; break
        case 'ControlLeft':
        case 'ControlRight':
        case 'KeyC':        droneInput.down = false; break
        case 'ShiftLeft':
        case 'ShiftRight':  droneInput.sprint = false; break
      }
    }
    // Focus loss never delivers the keyup: without this, alt-tabbing while
    // holding W leaves the drone flying until refocus.
    const onBlur = () => {
      droneInput.forward = droneInput.back = false
      droneInput.left = droneInput.right = false
      droneInput.up = droneInput.down = droneInput.sprint = false
      droneInput.pitchUp = droneInput.pitchDown = false
    }
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
      window.removeEventListener('blur', onBlur)
      // Release any held keys when the listener tears down.
      onBlur()
    }
  }, [droneActive, setDroneActive])

  if (!droneActive) return null

  return (
    <>
      <style jsx global>{`
        @keyframes drone-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Top-center: status */}
      <div
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
        style={{ animation: 'drone-fade-in 240ms ease-out' }}
      >
        <div
          className="px-4 py-2 flex items-center gap-2.5"
          style={{
            background: kawaii.cream,
            border: kawaii.outline,
            borderRadius: kawaii.radiusPill,
            boxShadow: kawaii.dropSm,
            fontFamily: ui.font,
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: '#276b3c', border: `2px solid ${kawaii.ink}`, width: 10, height: 10 }}
          />
          <span style={{ color: kawaii.ink, fontSize: 13, fontWeight: 800 }}>flying</span>
        </div>
      </div>

      {/* Bottom-center: controls cheat sheet */}
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
        style={{ animation: 'drone-fade-in 240ms ease-out 80ms both' }}
      >
        <div
          className="px-5 py-3 flex flex-wrap items-center justify-center gap-3.5 max-w-[92vw]"
          style={{
            background: kawaii.cream,
            border: kawaii.outline,
            borderRadius: kawaii.radius,
            boxShadow: kawaii.drop,
            fontFamily: ui.font,
          }}
        >
          <ControlPair keys={['W','S']} label="fly" />
          <ControlPair keys={['A','D']} label="turn" />
          <ControlPair keys={['↑','↓']} label="look" />
          <Sep />
          <ControlPair keys={['Space']} label="up" />
          <ControlPair keys={['Ctrl']} label="down" />
          <Sep />
          <ControlPair keys={['Shift']} label="sprint" />
          <Sep />
          <ControlPair keys={['Esc']} label="exit" />
        </div>
      </div>
    </>
  )
}

function ControlPair({ keys, label }) {
  return (
    <span className="flex items-center gap-1.5">
      {keys.map((k, i) => (
        <KeyCap key={i}>{k}</KeyCap>
      ))}
      <span style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12, fontWeight: 700, marginLeft: 2 }}>
        {label}
      </span>
    </span>
  )
}

function KeyCap({ children }) {
  return (
    <span
      style={{
        color: kawaii.ink,
        fontFamily: ui.font,
        fontSize: 11,
        fontWeight: 800,
        border: kawaii.outlineThin,
        background: kawaii.creamDeep,
        padding: '3px 7px',
        minWidth: 20,
        textAlign: 'center',
        borderRadius: 7,
        boxShadow: '0 2px 0 rgba(47,38,80,0.22)',
      }}
    >
      {children}
    </span>
  )
}

function Sep() {
  return <span style={{ color: 'rgba(47,38,80,0.3)', fontSize: 12 }}>·</span>
}
